import { Order, OrderItems } from '../../../store/order/api/interface';
import { ORDER_STATUS } from '../../../constants';

const formatLabel = (labelEn?: string, labelAr?: string) => {
  if (!labelEn && !labelAr) return '';
  if (labelEn && labelAr) return `${labelEn} (${labelAr})`;
  return labelEn || labelAr || '';
};

const getCardMessage = (cardMessage: Order['cardMessage']) => {
  if (!cardMessage) return '';
  if (typeof cardMessage === 'string') return cardMessage;
  return cardMessage?.message ?? '';
};

const formatDateOnly = (value: unknown) => {
  if (!value) return '';

  const dateCandidate =
    typeof value === 'object' && value !== null && 'date' in (value as any)
      ? (value as any).date
      : value;

  const d = new Date(dateCandidate as any);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Qatar',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);

  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';

  return day && month && year ? `${day}/${month}/${year}` : '';
};

const formatDateTime = (value: unknown) => {
  if (!value) return '';

  const d = new Date(value as any);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Qatar',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);

  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';

  if (!day || !month || !year) return '';

  return `${day}/${month}/${year} ${hour}:${minute} ${dayPeriod}`.trim();
};

const getOrderStatusLabel = (status?: string) => {
  if (!status) return '';
  const matchedStatus = ORDER_STATUS.find((item) => item.value === status);
  return matchedStatus?.name ?? status;
};

const getPrintImageUrl = (printImageUrl?: string | null) => {
  if (!printImageUrl) return '';

  try {
    const parsed = JSON.parse(printImageUrl);
    if (parsed && typeof parsed === 'object') {
      return parsed.printImageUrl || parsed.customImageUrl || '';
    }
  } catch {
    // plain URL string
  }

  return printImageUrl;
};

const formatCountryPaymentMethod = (method: Order['country_payment_method']) => {
  if (!method) return '';
  return formatLabel(method.labelEn, method.labelAr) || method.code || '';
};

const formatPaymentFields = (payment: any) => {
  if (!payment) {
    return {
      paymentStatus: '',
      paymentAmount: '',
      paymentDate: '',
    };
  }

  const status = payment.paymentStatus ?? '';
  const amount =
    payment.paymentAmount !== undefined && payment.paymentAmount !== null
      ? parseFloat(Number(payment.paymentAmount).toFixed(2))
      : '';

  return {
    paymentStatus: status,
    paymentAmount: amount,
    paymentDate: formatDateOnly(payment.paymentDate),
  };
};

const formatPromoCodeUsage = (promoUsage: Order['promo_code_usage']) => {
  if (!promoUsage?.promo_code) return '';

  const promo = promoUsage.promo_code;
  const code = promo.code ?? '';
  const discountType = promo.discountType ?? '';
  const discountAmount = promo.discountAmount ?? '';

  if (!code) return '';

  if (discountType && discountAmount !== '') {
    return `${code} (${discountType}: ${discountAmount})`;
  }

  return code;
};

const buildOrderAddress = (element: Order) =>
  `${element?.shippingAddress?.addressName ?? ''} , Building No: ${element?.shippingAddress?.buildingNumber ?? ''}, Street No: ${element?.shippingAddress?.street ?? ''},  Zone: ${element?.shippingAddress?.area ?? ''},  Region: ${element?.shippingAddress?.region?.labelEn ?? ''} (${element?.shippingAddress?.region?.labelAr ?? ''}),  Country: ${element?.shippingAddress?.country?.labelEn ?? ''} (${element?.shippingAddress?.country?.labelAr ?? ''})`;

const buildItemRow = (element: Order, orderItem?: OrderItems) => {
  const orderCake = orderItem?.order_cake;
  const addonProduct = orderItem?.addon_product;
  const qty = orderItem ? Number(orderItem.qty) : 0;
  const unitPrice = orderItem ? Number(orderItem.unitPrice) : 0;
  const deliveryDate = element?.deliveryDate as
    | { date?: string; time?: string; type?: string }
    | undefined;
  const paymentFields = formatPaymentFields((element as any)?.payment);

  return {
    number: element?.orderNumber,
    date: formatDateTime(element?.orderDate),
    address: buildOrderAddress(element),
    user: `${element?.userDetails?.firstName ?? ''} ${element?.userDetails?.lastName ?? ''}`.trim(),
    status: getOrderStatusLabel(element?.status),
    additionalInfo: element?.additionalInfo ?? '',
    cardMessage: getCardMessage(element?.cardMessage),
    totalCardItems: element?.order_items?.length ?? 0,
    totalAmount: parseFloat(Number(element?.totalAmount ?? 0).toFixed(2)),
    isIdentitySecret: element?.isIdentitySecret ? 'Yes' : 'No',
    promoCodeDiscountAmount: parseFloat(
      Number(element?.promoCodeDiscountAmount ?? 0).toFixed(2)
    ),
    vatAmount: parseFloat(Number(element?.vatAmount ?? 0).toFixed(2)),
    deliveryFee: parseFloat(Number(element?.deliveryFee ?? 0).toFixed(2)),
    deliveryDate: formatDateOnly(deliveryDate),
    deliveryTime: deliveryDate?.time ?? '',
    exactDeliverySlot: element?.exactDeliverySlot ?? '',
    exactDeliverySlotDate: formatDateOnly(element?.exactDeliverySlotDate),
    countryPaymentMethod: formatCountryPaymentMethod(element?.country_payment_method),
    paymentStatus: paymentFields.paymentStatus,
    paymentAmount: paymentFields.paymentAmount,
    paymentDate: paymentFields.paymentDate,
    promoCodeUsage: formatPromoCodeUsage(element?.promo_code_usage),
    createdAt: formatDateTime(element?.createdAt),
    updatedAt: formatDateTime(element?.updatedAt),
    createdBy:
      (element as any)?.createdByUser?.trim() || element?.createdBy || '',
    updatedBy:
      (element as any)?.updatedByUser?.trim() || element?.updatedBy || '',
    itemType: orderCake ? 'Cake' : addonProduct ? 'Addon' : '',
    productName: orderCake
      ? `${orderCake?.shapeLabelEn ?? orderCake?.shape?.labelEn ?? ''} (${orderCake?.flavorLabelEn ?? orderCake?.flavor?.labelEn ?? ''})`
      : addonProduct?.labelEn ?? '',
    shape: orderCake
      ? formatLabel(
          orderCake?.shape?.labelEn ?? orderCake?.shapeLabelEn,
          orderCake?.shape?.labelAr ?? orderCake?.shapeLabelAr
        )
      : '',
    flavor: orderCake
      ? formatLabel(
          orderCake?.flavor?.labelEn ?? orderCake?.flavorLabelEn,
          orderCake?.flavor?.labelAr ?? orderCake?.flavorLabelAr
        )
      : '',
    topping: orderCake?.topping_category_item
      ? formatLabel(
          orderCake.topping_category_item.labelEn,
          orderCake.topping_category_item.labelAr
        )
      : '',
    toppingCategory: orderCake?.topping_category_item?.topping_category
      ? formatLabel(
          orderCake.topping_category_item.topping_category.labelEn,
          orderCake.topping_category_item.topping_category.labelAr
        )
      : '',
    categoryCake: orderCake?.category_cake
      ? formatLabel(orderCake.category_cake.labelEn, orderCake.category_cake.labelAr)
      : '',
    color: orderCake?.colorCode ?? '',
    writeOnCake: orderCake?.writeOnCake ?? '',
    instruction: orderCake?.instruction ?? '',
    printImageUrl: getPrintImageUrl(orderCake?.printImageUrl),
    qty: orderItem?.qty ?? '',
    unitPrice: orderItem ? parseFloat(unitPrice.toFixed(2)) : '',
    lineTotal: orderItem
      ? parseFloat(Number(unitPrice * qty).toFixed(2))
      : '',
  };
};

export function csv(data: Order[]) {
  const result = [];

  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    const orderItems = element?.order_items ?? [];

    if (orderItems.length === 0) {
      result.push(buildItemRow(element));
      continue;
    }

    for (let itemIndex = 0; itemIndex < orderItems.length; itemIndex++) {
      result.push(buildItemRow(element, orderItems[itemIndex]));
    }
  }

  return result;
}
